/**
 * App shell: header, routes, offline strip, comparison tray.
 */

import { Link, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { AppStateProvider, useAppState, useOnlineStatus } from '@/state/AppState';
import { CompareTray } from '@/components/Panels';
import { LeaderboardPage } from '@/pages/LeaderboardPage';
import { SearchPage } from '@/pages/SearchPage';
import { ProductPage } from '@/pages/ProductPage';
import { ComparePage } from '@/pages/ComparePage';
import { SettingsPage } from '@/pages/SettingsPage';
import { LegalPage } from '@/pages/LegalPage';

export default function App(): React.JSX.Element {
  return (
    <AppStateProvider>
      <Shell />
    </AppStateProvider>
  );
}

function Shell(): React.JSX.Element {
  const online = useOnlineStatus();
  const { compareIds, compareName, removeCompare, clearCompare } = useAppState();
  const location = useLocation();

  // Names are remembered when a product is added, so the tray stays readable
  // after the originating result list unmounts.
  const trayItems = compareIds.map((id) => ({
    id,
    name: compareName(id) ?? shortenId(id),
  }));

  return (
    <div className="app">
      {!online && (
        <div className="status-strip" role="status">
          <span className="dot dot-warn" aria-hidden="true" />
          You are offline. Any data shown was cached earlier and may be out of date.
        </div>
      )}

      <header className="app-header">
        <div className="header-inner">
          <Link className="brand" to="/">
            <span className="brand-mark" aria-hidden="true">
              K
            </span>
            KcalTrack
          </Link>

          <nav className="header-nav" aria-label="Main">
            <NavLink className="nav-link" to="/" end>
              Rankings
            </NavLink>
            <NavLink className="nav-link" to="/compare">
              Compare
              {compareIds.length > 0 && <span className="filter-badge">{compareIds.length}</span>}
            </NavLink>
            <NavLink className="nav-link" to="/settings">
              Targets
            </NavLink>
          </nav>
        </div>
      </header>

      <main className="main" id="main">
        <Routes>
          <Route path="/" element={<LeaderboardPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/product/:id" element={<ProductPage />} />
          <Route path="/compare" element={<ComparePage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/legal" element={<LegalPage />} />
        <Route path="*" element={<NotFound />} />
        </Routes>
        <footer className="site-foot">
          <span>KcalTrack — independent. Not affiliated with any retailer.</span>
          <Link to="/legal#privacy">Privacy</Link>
          <Link to="/legal#terms">Terms</Link>
          <Link to="/legal#about">About</Link>
          <Link to="/legal#data">Data sources</Link>
          <Link to="/legal#contact">Contact</Link>
        </footer>
      </main>

      {location.pathname !== '/compare' && (
        <CompareTray items={trayItems} onRemove={removeCompare} onClear={clearCompare} />
      )}
    </div>
  );
}

function NotFound(): React.JSX.Element {
  return (
    <div className="empty-state">
      <h2>Page not found</h2>
      <Link className="btn btn-primary" to="/">
        Back to rankings
      </Link>
    </div>
  );
}

function shortenId(id: string): string {
  const bare = id.includes(':') ? id.slice(id.indexOf(':') + 1) : id;
  return bare.length > 14 ? `${bare.slice(0, 13)}…` : bare;
}
